// ── Conflicts Panel (Iter 31) ──────────────────────────────────────────────
// Two-tier conflict display:
//   • Personal — the provider's own self-overlaps (provider_double_book where
//     both sides are me). Treated as blocking issues; red palette.
//   • Shared school context — overlaps where I'm one of two parties (a
//     student of mine in another provider's session at the same time, or
//     we share a room). Treated as warnings; amber palette. Each shared
//     row is clearly labeled with the OTHER provider so the user
//     understands which party is theirs.
import { CalendarBlank, Warning, IdentificationCard } from "@phosphor-icons/react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CONFLICT_STYLES, DAYS, fmtTime } from "./utils";

export default function ConflictsPanel({
  personalConflicts = [],
  sharedConflicts = [],
  weekDates,
  // Legacy single-list prop kept for back-compat (admin-mode callers).
  conflicts,
}) {
  // If a caller still passes the flat `conflicts` prop, render it as one
  // mixed list to avoid breaking; new callers use personal/shared.
  const isPartitioned = !conflicts;
  const allEmpty = isPartitioned
    ? personalConflicts.length === 0 && sharedConflicts.length === 0
    : (conflicts || []).length === 0;

  if (allEmpty) {
    return (
      <div className="max-w-3xl space-y-3" data-testid="conflicts-panel">
        <div className="text-center py-12">
          <CalendarBlank size={32} weight="duotone" className="text-[#8FA998] mx-auto mb-2" />
          <p className="text-sm text-[#6B7280]">No conflicts on your schedule this week.</p>
          <p className="text-[11px] text-[#9B9890] mt-1">
            Your calendar is clean — no overlaps with school context to worry about either.
          </p>
        </div>
      </div>
    );
  }

  if (!isPartitioned) {
    // Back-compat path (rare).
    return (
      <div className="max-w-3xl space-y-3" data-testid="conflicts-panel">
        {(conflicts || []).map((c) => (
          <ConflictRow key={c.id} c={c} weekDates={weekDates} tone="personal" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-5" data-testid="conflicts-panel">
      {/* Personal */}
      <section data-testid="conflicts-personal-section">
        <header className="flex items-center gap-2 mb-2">
          <Warning size={14} weight="fill" className="text-[#B85C5C]" />
          <h3 className="text-sm font-semibold text-[#1F2937] heading-font">
            Your conflicts ({personalConflicts.length})
          </h3>
          <span className="text-[11px] text-[#6B7280]">
            Self-overlaps in your own schedule
          </span>
        </header>
        {personalConflicts.length === 0 ? (
          <p
            data-testid="conflicts-personal-empty"
            className="text-xs text-[#6B7280] italic px-4 py-3 bg-white rounded-xl border border-[#E5E3DB]"
          >
            No personal conflicts. Your own blocks don't overlap each other.
          </p>
        ) : (
          <div className="space-y-2">
            {personalConflicts.map((c) => (
              <ConflictRow key={c.id} c={c} weekDates={weekDates} tone="personal" />
            ))}
          </div>
        )}
      </section>

      {/* Shared */}
      <section data-testid="conflicts-shared-section">
        <header className="flex items-center gap-2 mb-2">
          <IdentificationCard size={14} weight="duotone" className="text-[#D9A05B]" />
          <h3 className="text-sm font-semibold text-[#1F2937] heading-font">
            Shared school context ({sharedConflicts.length})
          </h3>
          <span className="text-[11px] text-[#6B7280]">
            Other providers' sessions touching your students or rooms — warning only
          </span>
        </header>
        {sharedConflicts.length === 0 ? (
          <p
            data-testid="conflicts-shared-empty"
            className="text-xs text-[#6B7280] italic px-4 py-3 bg-white rounded-xl border border-[#E5E3DB]"
          >
            Nothing on the school calendar conflicts with your blocks this week.
          </p>
        ) : (
          <div className="space-y-2">
            {sharedConflicts.map((c) => (
              <ConflictRow key={c.id} c={c} weekDates={weekDates} tone="shared" />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ConflictRow({ c, weekDates, tone }) {
  const st = CONFLICT_STYLES[c.type] || CONFLICT_STYLES.student_overlap;
  const isShared = tone === "shared";
  // Shared rows use the amber/warning border to visually demote the row;
  // personal rows keep the original severity-coded palette.
  const borderClass = isShared ? "border-l-4 border-[#D9A05B]" : `border-l-4 ${st.border}`;
  const iconClass = isShared ? "text-[#D9A05B]" : st.text;
  const badgeClass = isShared
    ? "bg-[#FDF8F3] text-[#D9A05B] hover:bg-[#FDF8F3]"
    : `${st.bg} ${st.text} hover:bg-opacity-90`;
  return (
    <Card
      data-testid={`conflict-${c.id}`}
      data-tone={tone}
      className={`rounded-xl border shadow-sm ${borderClass}`}
    >
      <CardContent className="p-4 flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <Warning size={18} weight="fill" className={`${iconClass} mt-0.5 shrink-0`} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge className={`${badgeClass} text-[10px]`}>
                {isShared ? "shared · " : ""}
                {st.label}
              </Badge>
              <span className="text-[11px] text-[#6B7280]">
                {DAYS[(weekDates || []).indexOf(c.date)] || c.date}
              </span>
            </div>
            <p className="text-sm font-medium text-[#1F2937]">{c.description}</p>
            <p className="text-xs text-[#6B7280] mt-0.5 break-words">{c.detail}</p>
          </div>
        </div>
        <span className="text-xs font-medium text-[#1F2937] whitespace-nowrap">
          {fmtTime(c.time_range?.split("-")[0] || "")}
        </span>
      </CardContent>
    </Card>
  );
}
